import { ERC721InterfaceId, ERC1155InterfaceId } from '@mogul/shared';
import type { BigNumber, BigNumberish } from 'ethers';
import { useState } from 'react';

import type { MogulNFT } from '../lib/contracts';
import { MogulNFTFactory } from '../lib/contracts';
import { nftCatalogService } from '../lib/nfts/nft-catalog.service';
import { NFT_CONTRACT_ADDRESS } from '../lib/smart-contract-utils';
import { useContract } from './contract.hooks';

export interface MogulNFTToken {
  id: number;
  contractAddress: string;
  metadataRaw: string;
  metadata?: any;
}

export const useNFTContract = (props = { nftContractAddress: '' }) => {
  const { contract, ...walletDetails } = useContract<MogulNFT>(
    MogulNFTFactory,
    NFT_CONTRACT_ADDRESS,
    props.nftContractAddress,
  );

  const [nftBalances] = useState<
    Record<string, undefined | Record<string, number>>
  >({});

  const balanceOf = async (id: string) => {
    return contract?.balanceOf(walletDetails.address ?? '', id);
  };

  const getNftOwnerOfNft = async (
    addresses: string[],
    nftId: string,
  ): Promise<BigNumber[]> => {
    if (!contract) {
      return [];
    }

    return contract.balanceOfBatch(
      addresses,
      Array(addresses.length).fill(nftId),
    );
  };

  const getNFTMetadata = async (id: BigNumberish) => {
    const uri = await contract?.uri(id);

    if (uri != null) {
      return nftCatalogService.fetchAndParseMetadata(uri);
    }

    return {};
  };

  const getNftApprovalStatus = async (account: string, operator: string) => {
    if (account === '' || operator === '') {
      return false;
    }

    const approvalStatus = await contract?.isApprovedForAll(account, operator);

    return approvalStatus ?? false;
  };

  const setApprovalForAll = async (operator: string, approved: boolean) => {
    try {
      if (!contract) {
        return false;
      }

      const trx = await contract.setApprovalForAll(operator, approved);
      await trx.wait();

      return true;
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: { file: 'nft-contract.hooks.ts', function: 'setApprovalForAll' },
        contexts: {},
      });
    }
    return false;
  };

  const isApprovedForAll = async (owner: string, operator: string) => {
    try {
      if (!contract) {
        return false;
      }

      return await contract?.isApprovedForAll(owner, operator);
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: { file: 'nft-contract.hooks.ts', function: 'isApprovedForAll' },
        contexts: {},
      });
    }
  };

  const transferNft = async (
    from: string,
    to: string,
    id: BigNumberish,
    amount: BigNumberish,
  ) => {
    try {
      if (!contract) {
        return false;
      }

      const trx = await contract.safeTransferFrom(from, to, id, amount, '0x');
      await trx.wait();

      return true;
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: { file: 'nft-contract.hooks.ts', function: 'approveAnyContract' },
        contexts: {},
      });
    }
    return false;
  };

  const mintNft = async (
    recipientAddress: string,
    tokenID: BigNumberish,
    amount: BigNumberish,
    URI: string,
    data: any,
  ) => {
    try {
      if (!contract) {
        return false;
      }

      const mint = await contract.mintToken(
        recipientAddress,
        tokenID,
        amount,
        URI,
        data,
      );

      await mint.wait();

      return true;
    } catch (e) {
      console.error('minting failed', e);
      window.Sentry?.captureException(e, {
        tags: { file: 'nft-contract.hooks.ts', function: 'mintNft' },
        contexts: {},
      });
    }

    return false;
  };

  const isErc1155 = async () => {
    if (!contract) {
      return false;
    }

    return contract.supportsInterface(ERC1155InterfaceId);
  };

  const isErc721 = async () => {
    if (!contract) {
      return false;
    }

    return contract.supportsInterface(ERC721InterfaceId);
  };

  return {
    initialized: contract !== undefined,
    walletInfo: walletDetails,
    nftBalances:
      nftBalances[`${walletDetails.address ?? ''}_${walletDetails.chain.type}`],
    setApprovalForAll,
    isApprovedForAll,
    getNftApprovalStatus,
    getNFTMetadata,
    getNftOwnerOfNft,
    transferNft,
    balanceOf,
    mintNft,
    isErc1155,
    isErc721,
  };
};
